import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import styles from './Gebruikersbeheer.module.css';

export default function Gebruikersbeheer() {
  const { heeftPermissie, gebruiker: ingelogd } = useAuth();
  const isSuperadmin = heeftPermissie('superadmin.alles');

  const [gebruikers, setGebruikers]   = useState([]);
  const [rollen, setRollen]           = useState([]);
  const [organisaties, setOrganisaties] = useState([]);
  const [locaties, setLocaties]             = useState([]);
  const [bewerkLocaties, setBewerkLocaties] = useState([]);
  const [bewerkAfdelingen, setBewerkAfdelingen] = useState([]);
  const [afdelingen, setAfdelingen]   = useState([]);
  const [geselecteerdeOrg, setGeselecteerdeOrg]   = useState('');
  const [filterLocatie, setFilterLocatie]         = useState('');
  const [laden, setLaden]             = useState(true);
  const [zoekterm, setZoekterm]       = useState('');
  const [toonModal, setToonModal]     = useState(false);
  const [toonBulk, setToonBulk]       = useState(false);

  const leegForm = { email:'', naam:'', rol_id:'', locatie_id:'', afdeling_id:'', organisatie_id:'' };
  const [bewerkGebruiker, setBewerkGebruiker] = useState(null);
  const [toonBewerkModal, setToonBewerkModal] = useState(false);
  const [bewerkForm, setBewerkForm]           = useState({ naam:'', rol_id:'', locatie_id:'', afdeling_id:'' });
  const [form, setForm]               = useState(leegForm);
  const [formLaden, setFormLaden]     = useState(false);
  const [bulkTekst, setBulkTekst]     = useState('');
  const [bulkRolId, setBulkRolId]     = useState('');

  const laadData = useCallback(async (orgId) => {
    setLaden(true);
    try {
      const params = orgId ? { organisatie_id: orgId } : {};
      const [gRes, rRes] = await Promise.all([
        axios.get('/api/gebruikers', { params }),
        axios.get('/api/gebruikers/rollen', { params }),
      ]);
      setGebruikers(gRes.data.gebruikers);
      setRollen(rRes.data.rollen);
    } catch {
      toast.error('Gegevens laden mislukt.');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperadmin) {
      axios.get('/api/organisaties')
        .then(r => setOrganisaties(r.data.organisaties))
        .catch(() => {});
    } else {
      laadData();
    }
  }, [isSuperadmin, laadData]);

  useEffect(() => {
    if (isSuperadmin && geselecteerdeOrg) {
      laadData(geselecteerdeOrg);
    }
  }, [geselecteerdeOrg, isSuperadmin, laadData]);

  // Locaties laden bij org keuze
  async function laadLocaties(orgId) {
    if (!orgId) { setLocaties([]); setAfdelingen([]); return; }
    try {
      const res = await axios.get('/api/gebruikers/locaties', { params: { organisatie_id: orgId } });
      setLocaties(res.data.locaties);
      setAfdelingen([]);
    } catch { setLocaties([]); }
  }

  // Afdelingen laden bij locatie keuze
  async function laadAfdelingen(locatieId) {
    if (!locatieId) { setAfdelingen([]); return; }
    try {
      const res = await axios.get('/api/gebruikers/afdelingen', { params: { locatie_id: locatieId } });
      setAfdelingen(res.data.afdelingen);
    } catch { setAfdelingen([]); }
  }

  function openModal() {
    setForm({ ...leegForm, organisatie_id: geselecteerdeOrg || '' });
    if (geselecteerdeOrg) laadLocaties(geselecteerdeOrg);
    setToonModal(true);
  }

  const gefilterd = gebruikers.filter(g => {
    const zoekMatch = !zoekterm ||
      g.naam?.toLowerCase().includes(zoekterm.toLowerCase()) ||
      g.email.toLowerCase().includes(zoekterm.toLowerCase()) ||
      g.rol_naam?.toLowerCase().includes(zoekterm.toLowerCase());
    const locatieMatch = !filterLocatie || g.locatie_id?.toString() === filterLocatie;
    return zoekMatch && locatieMatch;
  });

  async function handleAanmaken(e) {
    e.preventDefault();
    setFormLaden(true);
    try {
      const payload = { ...form };
      if (isSuperadmin && geselecteerdeOrg) payload.organisatie_id = geselecteerdeOrg;
      const res = await axios.post('/api/gebruikers', payload);
      toast.success('Gebruiker aangemaakt. Uitnodigingslink gekopieerd naar klembord.');
      navigator.clipboard?.writeText(res.data.uitnodiging_url).catch(() => {});
      setToonModal(false);
      setForm(leegForm);
      laadData(geselecteerdeOrg || undefined);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Aanmaken mislukt.');
    } finally {
      setFormLaden(false);
    }
  }

  async function openBewerken(gebruiker) {
    setBewerkGebruiker(gebruiker);
    setBewerkForm({
      naam:        gebruiker.naam || '',
      rol_id:      gebruiker.rol_id || '',
      locatie_id:  gebruiker.locatie_id || '',
      afdeling_id: gebruiker.afdeling_id || '',
    });
    // Laad locaties direct in eigen state
    const orgId = gebruiker.organisatie_id || geselecteerdeOrg || ingelogd?.organisatie_id;
    if (orgId) {
      try {
        const res = await axios.get('/api/gebruikers/locaties', { params: { organisatie_id: orgId } });
        setBewerkLocaties(res.data.locaties || []);
      } catch { setBewerkLocaties([]); }
    } else {
      setBewerkLocaties([]);
    }
    if (gebruiker.locatie_id) {
      try {
        const res = await axios.get('/api/gebruikers/afdelingen', { params: { locatie_id: gebruiker.locatie_id } });
        setBewerkAfdelingen(res.data.afdelingen || []);
      } catch { setBewerkAfdelingen([]); }
    } else {
      setBewerkAfdelingen([]);
    }
    setToonBewerkModal(true);
  }

  async function handleBewerkOpslaan(e) {
    e.preventDefault();
    setFormLaden(true);
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      await axios.put(`/api/gebruikers/${bewerkGebruiker.id}`, bewerkForm, { params });
      toast.success('Gebruiker bijgewerkt.');
      setToonBewerkModal(false);
      laadData(geselecteerdeOrg || undefined);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bijwerken mislukt.');
    } finally {
      setFormLaden(false);
    }
  }

  async function handleSetActief(id, actief) {
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      await axios.patch(`/api/gebruikers/${id}/actief`, { actief }, { params });
      toast.success(actief ? 'Gebruiker geactiveerd.' : 'Gebruiker gedeactiveerd.');
      laadData(geselecteerdeOrg || undefined);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Status wijzigen mislukt.');
    }
  }

  async function handleVerwijderen(id, email) {
    if (!window.confirm(`Weet u zeker dat u ${email} wilt verwijderen?`)) return;
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      await axios.delete(`/api/gebruikers/${id}`, { params });
      toast.success('Gebruiker verwijderd.');
      laadData(geselecteerdeOrg || undefined);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  async function handleUitnodigingOpnieuw(id) {
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      const res = await axios.post(`/api/gebruikers/${id}/uitnodiging`, {}, { params });
      navigator.clipboard?.writeText(res.data.uitnodiging_url).catch(() => {});
      toast.success('Uitnodiging verstuurd. Link gekopieerd naar klembord.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Versturen mislukt.');
    }
  }

  async function handleBulk(e) {
    e.preventDefault();
    if (!bulkRolId) return toast.error('Selecteer een rol voor de bulk import.');
    const regels = bulkTekst.split('\n').map(r => r.trim()).filter(r => r.length > 0);
    const gebruikersData = regels.map(r => {
      const [email, naam] = r.split(',').map(s => s.trim());
      return { email, naam: naam || null, rol_id: parseInt(bulkRolId) };
    });
    try {
      const payload = { gebruikers: gebruikersData };
      if (isSuperadmin && geselecteerdeOrg) payload.organisatie_id = geselecteerdeOrg;
      const res = await axios.post('/api/gebruikers/bulk', payload);
      const aangemaakt   = res.data.resultaten.filter(r => r.status === 'aangemaakt').length;
      const overgeslagen = res.data.resultaten.filter(r => r.status === 'overgeslagen').length;
      toast.success(`${aangemaakt} aangemaakt, ${overgeslagen} overgeslagen.`);
      setToonBulk(false);
      setBulkTekst('');
      laadData(geselecteerdeOrg || undefined);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk import mislukt.');
    }
  }

  // Superadmin zonder organisatiekeuze
  if (isSuperadmin && !geselecteerdeOrg) {
    return (
      <div className={styles.pagina}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.titel}>Gebruikersbeheer</h1>
            <p className={styles.subtitel}>Kies een organisatie om gebruikers te beheren</p>
          </div>
        </div>
        <div className={styles.orgKeuze}>
          {organisaties.map(org => (
            <button key={org.id} className={styles.orgKnop}
              onClick={() => setGeselecteerdeOrg(org.id.toString())}>
              <div className={styles.orgKleurBolletje} style={{ background: org.primaire_kleur || '#1B4F8A' }} />
              <div>
                <div className={styles.orgNaamTekst}>{org.naam}</div>
                <div className={styles.orgMeta}>{org.aantal_gebruikers || 0} gebruiker(s)</div>
              </div>
              <span className={styles.orgPijl}>→</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const geselecteerdeOrgNaam = organisaties.find(o => o.id.toString() === geselecteerdeOrg)?.naam;

  return (
    <div className={styles.pagina}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.titel}>Gebruikersbeheer</h1>
          <p className={styles.subtitel}>
            {geselecteerdeOrgNaam ? `${geselecteerdeOrgNaam} — ` : ''}{gebruikers.length} gebruiker(s)
          </p>
        </div>
        <div className={styles.headerKnoppen}>
          {isSuperadmin && (
            <button className={styles.knopSecundair} onClick={() => setGeselecteerdeOrg('')}>
              ← Andere organisatie
            </button>
          )}
          <button className={styles.knopSecundair} onClick={() => setToonBulk(true)}>Bulk importeren</button>
          <button className={styles.knopPrimair} onClick={openModal}>+ Gebruiker toevoegen</button>
        </div>
      </div>

      <div className={styles.zoekWrap}>
        <input type="text" placeholder="Zoeken op naam, e-mail of rol..."
          value={zoekterm} onChange={e => setZoekterm(e.target.value)}
          className={styles.zoekInput} />
        {locaties.length > 0 && (
          <select value={filterLocatie} onChange={e => setFilterLocatie(e.target.value)}
            className={styles.filterSelect}>
            <option value="">Alle locaties</option>
            {locaties.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
          </select>
        )}
      </div>

      {laden ? (
        <div style={{ textAlign:'center', padding:32, color:'#999', fontFamily:'Arial' }}>Laden...</div>
      ) : (
        <div className={styles.tabelWrap}>
          <table className={styles.tabel}>
            <thead>
              <tr>
                <th>Naam / E-mail</th><th>Rol</th><th>Locatie / Afdeling</th><th>Status</th>
                <th>2FA</th><th>Laatste login</th><th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {gefilterd.length === 0 && (
                <tr><td colSpan={7} className={styles.leeg}>Geen gebruikers gevonden.</td></tr>
              )}
              {gefilterd.map(g => (
                <tr key={g.id}>
                  <td>
                    <div className={styles.naamCell}>
                      <span className={styles.naam}>{g.naam || '—'}</span>
                      <span className={styles.email}>{g.email}</span>
                    </div>
                  </td>
                  <td><span className={styles.rolBadge}>{g.rol_naam}</span></td>
                  <td>
                    {g.locatie_naam ? (
                      <div>
                        <div style={{fontSize:13,color:'#1A1A2E',fontFamily:'Arial'}}>{g.locatie_naam}</div>
                        {g.afdeling_naam && <div style={{fontSize:11,color:'#888',fontFamily:'Arial'}}>{g.afdeling_naam}</div>}
                      </div>
                    ) : <span style={{color:'#ccc'}}>—</span>}
                  </td>
                  <td>
                    {g.actief ? <span className={styles.badgeGroen}>Actief</span>
                      : g.uitgenodigd ? <span className={styles.badgeOranje}>Uitgenodigd</span>
                      : <span className={styles.badgeGrijs}>Inactief</span>}
                  </td>
                  <td>{g.twofa_actief ? <span className={styles.badgeGroen}>✓</span> : <span className={styles.badgeGrijs}>—</span>}</td>
                  <td className={styles.datumCell}>{g.laatste_login ? new Date(g.laatste_login).toLocaleDateString('nl-NL') : '—'}</td>
                  <td>
                    <div className={styles.actieKnoppen}>
                      <button className={styles.knopKlein} onClick={() => openBewerken(g)}>Bewerken</button>
                      {!g.actief && g.uitgenodigd && (
                        <button className={styles.knopKlein} onClick={() => handleUitnodigingOpnieuw(g.id)}>Opnieuw uitnodigen</button>
                      )}
                      <button className={styles.knopKlein} onClick={() => handleSetActief(g.id, !g.actief)}>
                        {g.actief ? 'Deactiveren' : 'Activeren'}
                      </button>
                      <button className={`${styles.knopKlein} ${styles.knopRood}`} onClick={() => handleVerwijderen(g.id, g.email)}>Verwijderen</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: gebruiker toevoegen */}
      {toonModal && (
        <div className={styles.overlay} onClick={() => setToonModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>Gebruiker toevoegen</h2>
            <form onSubmit={handleAanmaken} className={styles.form}>
              <div className={styles.veld}>
                <label>E-mailadres *</label>
                <input type="email" required value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="naam@organisatie.nl" />
              </div>
              <div className={styles.veld}>
                <label>Naam</label>
                <input type="text" value={form.naam}
                  onChange={e => setForm({ ...form, naam: e.target.value })}
                  placeholder="Voornaam Achternaam" />
              </div>
              <div className={styles.veld}>
                <label>Rol *</label>
                <select required value={form.rol_id}
                  onChange={e => setForm({ ...form, rol_id: e.target.value })}>
                  <option value="">Kies een rol...</option>
                  {rollen.filter(r => r.naam !== 'Superadmin').map(r => (
                    <option key={r.id} value={r.id}>{r.naam}</option>
                  ))}
                </select>
              </div>
              {locaties.length > 0 && (
                <div className={styles.veld}>
                  <label>Locatie <span className={styles.optioneel}>(optioneel)</span></label>
                  <select value={form.locatie_id}
                    onChange={e => { setForm({ ...form, locatie_id: e.target.value, afdeling_id: '' }); laadAfdelingen(e.target.value); }}>
                    <option value="">Geen locatie</option>
                    {locaties.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
                  </select>
                </div>
              )}
              {afdelingen.length > 0 && (
                <div className={styles.veld}>
                  <label>Afdeling <span className={styles.optioneel}>(optioneel)</span></label>
                  <select value={form.afdeling_id}
                    onChange={e => setForm({ ...form, afdeling_id: e.target.value })}>
                    <option value="">Geen afdeling</option>
                    {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
                  </select>
                </div>
              )}
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair} onClick={() => setToonModal(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair} disabled={formLaden}>
                  {formLaden ? 'Aanmaken...' : 'Aanmaken & uitnodigen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: bulk import */}
      {toonBulk && (
        <div className={styles.overlay} onClick={() => setToonBulk(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>Bulk importeren</h2>
            <p className={styles.uitleg}>Één e-mailadres per regel. Optioneel naam achter een komma.<br />
              <code>naam@organisatie.nl, Jan Jansen</code></p>
            <form onSubmit={handleBulk} className={styles.form}>
              <div className={styles.veld}>
                <label>Rol voor alle gebruikers *</label>
                <select required value={bulkRolId} onChange={e => setBulkRolId(e.target.value)}>
                  <option value="">Kies een rol...</option>
                  {rollen.filter(r => r.naam !== 'Superadmin').map(r => (
                    <option key={r.id} value={r.id}>{r.naam}</option>
                  ))}
                </select>
              </div>
              <div className={styles.veld}>
                <label>E-mailadressen</label>
                <textarea rows={8} value={bulkTekst} onChange={e => setBulkTekst(e.target.value)}
                  placeholder={'naam1@org.nl, Jan Jansen\nnaam2@org.nl'} className={styles.textarea} />
              </div>
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair} onClick={() => setToonBulk(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair}>Importeren</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bewerk modal */}
      {toonBewerkModal && bewerkGebruiker && (
        <div className={styles.overlay} onClick={() => setToonBewerkModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>{bewerkGebruiker.naam || bewerkGebruiker.email} bewerken</h2>
            <form onSubmit={handleBewerkOpslaan}>
              <div className={styles.veld}>
                <label>Naam</label>
                <input type="text" value={bewerkForm.naam}
                  onChange={e => setBewerkForm({ ...bewerkForm, naam: e.target.value })}
                  placeholder="Voornaam Achternaam" />
              </div>
              <div className={styles.veld}>
                <label>Rol *</label>
                <select required value={bewerkForm.rol_id}
                  onChange={e => setBewerkForm({ ...bewerkForm, rol_id: e.target.value })}>
                  <option value="">Kies een rol...</option>
                  {rollen.filter(r => r.naam !== 'Superadmin').map(r => (
                    <option key={r.id} value={r.id}>{r.naam}</option>
                  ))}
                </select>
              </div>
              {bewerkLocaties.length > 0 && (
                <div className={styles.veld}>
                  <label>Locatie <span className={styles.optioneel}>(optioneel)</span></label>
                  <select value={bewerkForm.locatie_id}
                    onChange={async e => {
                      const locId = e.target.value;
                      setBewerkForm({ ...bewerkForm, locatie_id: locId, afdeling_id: '' });
                      if (locId) {
                        try {
                          const res = await axios.get('/api/gebruikers/afdelingen', { params: { locatie_id: locId } });
                          setBewerkAfdelingen(res.data.afdelingen || []);
                        } catch { setBewerkAfdelingen([]); }
                      } else {
                        setBewerkAfdelingen([]);
                      }
                    }}>
                    <option value="">Geen locatie</option>
                    {bewerkLocaties.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
                  </select>
                </div>
              )}
              {bewerkAfdelingen.length > 0 && (
                <div className={styles.veld}>
                  <label>Afdeling <span className={styles.optioneel}>(optioneel)</span></label>
                  <select value={bewerkForm.afdeling_id}
                    onChange={e => setBewerkForm({ ...bewerkForm, afdeling_id: e.target.value })}>
                    <option value="">Geen afdeling</option>
                    {bewerkAfdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
                  </select>
                </div>
              )}
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair}
                  onClick={() => setToonBewerkModal(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair} disabled={formLaden}>
                  {formLaden ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
